import type { Meta, StoryObj } from '@storybook/angular';
import { expect } from 'storybook/test';
import { GrapesIconComponent } from '../../../app/core/svg-icons/grapes-icon.component';

const meta: Meta<GrapesIconComponent> = {
  title: 'Atoms/Icons/GrapesIcon',
  component: GrapesIconComponent,
  parameters: {
    docs: {
      description: {
        component: 'A grapes icon component. Perfect for wine-related content, vineyard information, and representing wine products.',
      },
    },
  },
};

export default meta;
type Story = StoryObj<GrapesIconComponent>;

export const Default: Story = {
  render: () => ({
    template: '<app-grapes-icon></app-grapes-icon>',
  }),
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};

export const WineCategories: Story = {
  render: () => ({
    template: `
      <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(200px, 1fr)); gap: 1rem; max-width: 800px;">
        
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; text-align: center; transition: transform 0.2s; cursor: pointer;" 
             onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: #5C0A31;">
            <app-grapes-icon></app-grapes-icon>
          </div>
          <h4 style="margin: 0 0 0.5rem 0; color: #5C0A31;">Rødvin</h4>
          <p style="margin: 0; color: #666; font-size: 0.875rem;">Udforsk vores udvalg af røde vine</p>
        </div>
        
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; text-align: center; transition: transform 0.2s; cursor: pointer;" 
             onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: #DAA520;">
            <app-grapes-icon></app-grapes-icon>
          </div>
          <h4 style="margin: 0 0 0.5rem 0; color: #5C0A31;">Hvidvin</h4>
          <p style="margin: 0; color: #666; font-size: 0.875rem;">Friske og elegante hvide vine</p>
        </div>
        
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; text-align: center; transition: transform 0.2s; cursor: pointer;" 
             onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: #E91E63;">
            <app-grapes-icon></app-grapes-icon>
          </div>
          <h4 style="margin: 0 0 0.5rem 0; color: #5C0A31;">Rosé</h4>
          <p style="margin: 0; color: #666; font-size: 0.875rem;">Lette og forfriskende rosé vine</p>
        </div>
        
        <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1.5rem; text-align: center; transition: transform 0.2s; cursor: pointer;" 
             onmouseover="this.style.transform='translateY(-2px)'" onmouseout="this.style.transform='translateY(0)'">
          <div style="display: flex; justify-content: center; margin-bottom: 1rem; color: #F39C12;">
            <app-grapes-icon></app-grapes-icon>
          </div>
          <h4 style="margin: 0 0 0.5rem 0; color: #5C0A31;">Champagne</h4>
          <p style="margin: 0; color: #666; font-size: 0.875rem;">Perler og festlige mousserende</p>
        </div>
        
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Grapes icons used to represent different wine categories with color coding.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};

export const VineyardInfo: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 2rem; max-width: 500px; background-color: #FAFAFA;">
        <div style="display: flex; align-items: flex-start; gap: 1.5rem;">
          <div style="color: #5C0A31; margin-top: 0.25rem;">
            <app-grapes-icon></app-grapes-icon>
          </div>
          <div>
            <h4 style="margin: 0 0 1rem 0; color: #5C0A31;">Fra vingården</h4>
            <p style="margin: 0 0 1rem 0; color: #666; line-height: 1.6;">
              Denne vin kommer fra de fineste druer, dyrket på sydvendte skråninger i Bordeaux-regionen. 
              Vingården har været i familien i over 200 år og anvender traditionelle metoder.
            </p>
            <div style="display: flex; gap: 2rem; font-size: 0.875rem;">
              <div>
                <strong>Årgang:</strong><br>
                <span style="color: #666;">2018</span>
              </div>
              <div>
                <strong>Alkohol:</strong><br>
                <span style="color: #666;">13.5%</span>
              </div>
              <div>
                <strong>Druer:</strong><br>
                <span style="color: #666;">Cabernet Sauvignon</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Grapes icon used in vineyard information section for wine details.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};

export const WineMakingProcess: Story = {
  render: () => ({
    template: `
      <div style="max-width: 600px;">
        <h3 style="margin-bottom: 2rem; color: #5C0A31;">Vores vinproduktion</h3>
        
        <div style="display: flex; flex-direction: column; gap: 2rem;">
          
          <div style="display: flex; align-items: center; gap: 1.5rem;">
            <div style="width: 60px; height: 60px; background-color: #5C0A31; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">
              1
            </div>
            <div style="flex: 1;">
              <h5 style="margin: 0 0 0.5rem 0;">Høst af druer</h5>
              <p style="margin: 0; color: #666;">Vi høster druerne på det optimale tidspunkt for at sikre den bedste kvalitet og smag.</p>
            </div>
            <div style="color: #5C0A31;">
              <app-grapes-icon></app-grapes-icon>
            </div>
          </div>
          
          <div style="display: flex; align-items: center; gap: 1.5rem;">
            <div style="width: 60px; height: 60px; background-color: #5C0A31; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">
              2
            </div>
            <div style="flex: 1;">
              <h5 style="margin: 0 0 0.5rem 0;">Sortering og presning</h5>
              <p style="margin: 0; color: #666;">Druerne sorteres omhyggeligt og presses forsigtigt for at udvinde den bedste saft.</p>
            </div>
            <div style="color: #8B4513;">
              <app-grapes-icon></app-grapes-icon>
            </div>
          </div>
          
          <div style="display: flex; align-items: center; gap: 1.5rem;">
            <div style="width: 60px; height: 60px; background-color: #5C0A31; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-weight: bold;">
              3
            </div>
            <div style="flex: 1;">
              <h5 style="margin: 0 0 0.5rem 0;">Fermentering</h5>
              <p style="margin: 0; color: #666;">Kontrolleret fermentering over flere uger udvikler vinens komplekse smagsprofil.</p>
            </div>
            <div style="color: #800020;">
              <app-grapes-icon></app-grapes-icon>
            </div>
          </div>
          
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Grapes icons used in wine making process timeline.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};

export const ProductBadge: Story = {
  render: () => ({
    template: `
      <div style="border: 1px solid #ddd; border-radius: 8px; padding: 1rem; max-width: 300px;">
        <div style="background-color: #f8f9fa; height: 150px; margin-bottom: 1rem; border-radius: 4px; display: flex; align-items: center; justify-content: center; color: #666;">
          [Vin billede]
        </div>
        <h5>Château Margaux 2016</h5>
        <p style="color: #666; margin: 0.5rem 0;">Bordeaux, Frankrig</p>
        
        <div style="display: flex; align-items: center; gap: 0.5rem; margin: 1rem 0; padding: 0.5rem; background-color: #F8F9FA; border-radius: 4px; border-left: 4px solid #5C0A31;">
          <app-grapes-icon></app-grapes-icon>
          <span style="font-size: 0.875rem; color: #5C0A31; font-weight: 500;">Premium druer fra egen vingård</span>
        </div>
        
        <div style="display: flex; justify-content: space-between; align-items: center;">
          <strong style="color: #5C0A31; font-size: 1.25rem;">2.499 kr</strong>
          <button style="background-color: #5C0A31; color: white; border: none; padding: 0.5rem 1rem; border-radius: 4px; cursor: pointer;">
            Tilføj til kurv
          </button>
        </div>
      </div>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Grapes icon used as a quality badge in product cards.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};

export const NavigationMenu: Story = {
  render: () => ({
    template: `
      <nav style="background-color: white; border-bottom: 1px solid #ddd; padding: 1rem;">
        <div style="display: flex; gap: 2rem; align-items: center;">
          <div style="font-weight: bold; font-size: 1.25rem; color: #5C0A31;">Supervin</div>
          
          <div style="display: flex; gap: 1.5rem; align-items: center;">
            <a href="#" style="display: flex; align-items: center; gap: 0.5rem; text-decoration: none; color: #333; padding: 0.5rem; border-radius: 4px; transition: background-color 0.2s;"
               onmouseover="this.style.backgroundColor='#f8f9fa'" onmouseout="this.style.backgroundColor='transparent'">
              <app-grapes-icon></app-grapes-icon>
              <span>Vine</span>
            </a>
            
            <a href="#" style="display: flex; align-items: center; gap: 0.5rem; text-decoration: none; color: #333; padding: 0.5rem; border-radius: 4px; transition: background-color 0.2s;"
               onmouseover="this.style.backgroundColor='#f8f9fa'" onmouseout="this.style.backgroundColor='transparent'">
              <span>Tilbud</span>
            </a>
            
            <a href="#" style="display: flex; align-items: center; gap: 0.5rem; text-decoration: none; color: #333; padding: 0.5rem; border-radius: 4px; transition: background-color 0.2s;"
               onmouseover="this.style.backgroundColor='#f8f9fa'" onmouseout="this.style.backgroundColor='transparent'">
              <span>Om os</span>
            </a>
          </div>
        </div>
      </nav>
    `,
  }),
  parameters: {
    docs: {
      description: {
        story: 'Grapes icon used in navigation menu to represent wine section.',
      },
    },
  },
  play: async ({ canvasElement }) => {
    await expect(canvasElement.querySelector('app-grapes-icon')).toBeTruthy();
  },
};
